import { useRouter } from "@tanstack/react-router";
import { Feather } from "@untitledui/icons";
import type { ReactNode } from "react";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { formatRelativeDay } from "@/utils/date-conversion";
import { pluralize } from "@/utils/text";
import type { PolicyRunAlert } from "./policy-run-alerts";

const TONE_STYLES = {
  warning: { panel: "bg-utility-error-50", headline: "text-utility-error-700", detail: "text-utility-error-600" },
  info: { panel: "bg-utility-blue-50", headline: "text-utility-blue-700", detail: "text-utility-blue-600" },
};

const AlertLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noopener" className="underline">
    {children}
  </a>
);

export const PolicyRunAlertPanel = ({ alert }: { alert: PolicyRunAlert }) => {
  const merchantId = useMerchant();
  const router = useRouter();

  const videoHref = (videoId: string) =>
    router.buildLocation({
      to: "/merchant/$merchantId/flagged-videos",
      params: { merchantId },
      search: { tab: "with-creator" as const, video_id: videoId },
    }).href;

  const runningVideosHref = router.buildLocation({
    to: "/merchant/$merchantId/flagged-videos",
    params: { merchantId },
    search: { tab: "with-creator" as const },
  }).href;

  const policyHref = (policyId: string) =>
    router.buildLocation({
      to: "/merchant/$merchantId/configure-safety",
      params: { merchantId },
      search: { policy: policyId },
    }).href;

  const tone = alert.kind === "activeSamePolicy" || alert.kind === "bulkActiveSamePolicy" ? "info" : "warning";
  const affectedVideos = (affected: number, total: number) => (
    <AlertLink href={runningVideosHref}>
      {affected} of {pluralize(total, "selected video", "selected videos")}
    </AlertLink>
  );

  const { headline, detail } = ((): { headline: ReactNode; detail: ReactNode } => {
    switch (alert.kind) {
      case "activeOtherPolicy":
        return {
          headline: (
            <>
              <AlertLink href={videoHref(alert.videoId)}>{alert.policyName}</AlertLink> is already running on this
              video.
            </>
          ),
          detail:
            "Adding another starts a second policy alongside it. If both message the creator, they'll get two messages.",
        };
      case "activeSamePolicy":
        return {
          headline: (
            <>
              <AlertLink href={videoHref(alert.videoId)}>{alert.policyName}</AlertLink> is already running on this
              video.
            </>
          ),
          detail:
            "Selecting it again won't start a new run or contact the creator — the existing run continues as normal.",
        };
      case "completedRun":
        return {
          headline: (
            <>
              <AlertLink href={videoHref(alert.videoId)}>{alert.policyName}</AlertLink> already ran on this video and
              finished {formatRelativeDay(alert.finishedAt)}.
            </>
          ),
          detail: "Running another policy contacts the creator again.",
        };
      case "bulkActiveOtherPolicy":
        return {
          headline: <>{affectedVideos(alert.affected, alert.total)} already have a policy running.</>,
          detail: `Running ${alert.selectedPolicyName} starts a second policy on those videos alongside the first. If both message the creator, they'll get two messages.`,
        };
      case "bulkActiveSamePolicy":
        return {
          headline: (
            <>
              {affectedVideos(alert.affected, alert.total)} are already running{" "}
              <AlertLink href={policyHref(alert.selectedPolicyId)}>{alert.selectedPolicyName}</AlertLink>.
            </>
          ),
          detail: `Those videos are skipped — the existing runs continue as normal.${
            alert.remaining > 0 ? ` The other ${alert.remaining} start a new run.` : ""
          }`,
        };
    }
  })();

  const styles = TONE_STYLES[tone];

  return (
    <div className={cx("flex flex-col gap-1 rounded-lg p-3", styles.panel)}>
      <p className={cx("flex items-start gap-1.5 text-sm font-medium leading-5", styles.headline)}>
        <Feather className="mt-0.5 size-4 shrink-0" />
        <span>{headline}</span>
      </p>
      <p className={cx("text-sm leading-5", styles.detail)}>{detail}</p>
    </div>
  );
};
